// src/pages/incidents/IncidentDetail.js - VERSION CORRIGÉE
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import Layout from "../../components/Layout";
import MapViewer from "../../components/MapViewer";

// Convertit une image distante en data URI base64 pour l'intégrer dans le PDF
// (jsPDF#addImage ne sait pas charger une URL directement de façon fiable).
const imageUrlToBase64 = (url) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = "Anonymous";
  img.onload = () => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    } catch (e) {
      resolve(null);
    }
  };
  img.onerror = () => resolve(null);
  img.src = url;
});

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',     // Bleu Sécurité
  secondary: '#1E88C8',   // Bleu Tech
  accent: '#4FC3F7',      // Bleu Clair
  darkText: '#2E2E2E',    // Gris Foncé
  lightBg: '#F5F7FA',     // Gris Clair
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3'
};

const statusLabel = (statut) => {
  switch (statut) {
    case 1:
      return { 
        text: "En cours", 
        style: {
          backgroundColor: '#fff3e0',
          color: '#f57c00'
        }
      };
    case 0:
    case 2:
    default:
      return { 
        text: "Terminée", 
        style: {
          backgroundColor: '#e8f5e9',
          color: '#2e7d32'
        }
      };
  }
};

const incidentStatutLabel = (statut, dateResolus) => {
  const isResolved = dateResolus !== null && dateResolus !== undefined && dateResolus !== "0000-00-00 00:00:00";
  
  if (isResolved) {
    return { 
      text: "Résolu", 
      style: {
        backgroundColor: '#e8f5e9',
        color: '#2e7d32'
      }
    };
  }
  
  if (!statut) return { 
    text: "Non défini", 
    style: {
      backgroundColor: '#F5F7FA',
      color: '#6c757d'
    }
  };
  
  const statutLower = statut.toLowerCase();
  if (statutLower.includes('résolu') || statutLower.includes('resolu')) {
    return { 
      text: "Résolu", 
      style: {
        backgroundColor: '#e8f5e9',
        color: '#2e7d32'
      }
    };
  }
  
  return { 
    text: "Non résolu", 
    style: {
      backgroundColor: '#fff3e0',
      color: '#f57c00'
    }
  };
};

const niveauLabel = (niveau) => {
  if (!niveau) return { 
    text: "Non défini", 
    style: {
      backgroundColor: '#F5F7FA',
      color: '#6c757d'
    }
  };
  
  const niveauId = niveau.id || niveau;
  const niveauText = niveau.label || niveau.libelle || 'N/A';
  
  switch (parseInt(niveauId)) {
    case 1:
      return { 
        text: niveauText,
        style: {
          backgroundColor: '#ffebee',
          color: '#c62828'
        }
      };
    case 2:
      return { 
        text: niveauText,
        style: {
          backgroundColor: '#fff3e0',
          color: '#f57c00'
        }
      };
    case 3:
      return {
        text: niveauText,
        style: {
          backgroundColor: '#e3f2fd',
          color: COLORS.secondary
        }
      };
    case 4:
      return {
        text: niveauText,
        style: {
          backgroundColor: '#e8f5e9',
          color: '#2e7d32'
        }
      };
    default:
      return { 
        text: niveauText,
        style: {
          backgroundColor: '#F5F7FA',
          color: '#6c757d'
        }
      };
  }
};

const formatDate = (dateString) => {
  if (!dateString || dateString === "0000-00-00" || dateString === "0000-00-00 00:00:00") {
    return "-";
  }
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return dateString;
  }
};

const IncidentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resolving, setResolving] = useState(false);
  const [apiError, setApiError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [societeLogo, setSocieteLogo] = useState(null);
  const [nomSociete, setNomSociete] = useState("");
  const [societeInfoLigne, setSocieteInfoLigne] = useState("");
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [actionResolutionText, setActionResolutionText] = useState("");
  const [resolveFieldError, setResolveFieldError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    axios.get(`${API_BASE_URL}/societes/my_logo.php`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (res.data?.logo_base64) setSocieteLogo(res.data.logo_base64);
      if (res.data?.nom_societe) setNomSociete(res.data.nom_societe);
      const ligne = [res.data?.address_societe, res.data?.tel_societe].filter(Boolean).join(" — ");
      if (ligne) setSocieteInfoLigne(ligne);
    }).catch(() => {});
  }, []);

  const fetchIncidentDetail = useCallback(async () => {
    if (!id || isNaN(parseInt(id))) {
      setError("ID d'incident invalide");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    setApiError("");
    setSuccessMessage("");
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${API_BASE_URL}/incidents/detail_incidents.php?id=${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          timeout: 10000
        }
      );

      if (response.data?.success) {
        setIncident(response.data.data);
      } else {
        setError(response.data?.message || "Impossible de charger les détails de l'incident.");
      }
    } catch (err) {      
      console.error("Erreur détaillée:", err);
      
      if (err.response) {
        switch (err.response.status) {
          case 400:
            setError("ID d'incident invalide ou format incorrect.");
            break;
          case 401:
            setError("Session expirée. Veuillez vous reconnecter.");
            localStorage.removeItem("access_token");
            setTimeout(() => navigate("/login"), 2000);
            break;
          case 403:
            setError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
            break;
          case 404:
            setError("Incident non trouvé.");
            break;
          case 500:
            setError("Erreur serveur. Veuillez contacter l'administrateur.");
            break;
          default:
            setError(`Erreur ${err.response.status}: ${err.response.data?.message || 'Erreur serveur'}`);
        }
      } else if (err.request) {
        setError("Serveur inaccessible. Vérifiez votre connexion.");
      } else {
        setError("Erreur lors de la requête.");
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const handleMarkAsResolved = () => {
    if (!incident || !incident.id_incident) {
      alert("ID d'incident invalide");
      return;
    }
    setActionResolutionText("");
    setResolveFieldError("");
    setShowResolveModal(true);
  };

  const confirmMarkAsResolved = async () => {
    const texte = actionResolutionText.trim();
    if (!texte) {
      setResolveFieldError("Merci de décrire les actions effectuées pour résoudre cet incident.");
      return;
    }

    setResolveFieldError("");
    setResolving(true);
    setApiError("");
    setSuccessMessage("");

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        alert("Session expirée. Veuillez vous reconnecter.");
        navigate("/login");
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/incidents/resolus_incidents.php`,
        {
          id_incident: incident.id_incident,
          est_resolu: true,
          action_resolution: texte
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          timeout: 10000
        }
      );
      
      if (response.data?.success) {
        setShowResolveModal(false);
        setActionResolutionText("");
        setSuccessMessage(response.data.message || "Incident marqué comme résolu avec succès !");
        fetchIncidentDetail();

        setTimeout(() => {
          setSuccessMessage("");
        }, 3000);
      } else {
        setResolveFieldError(response.data?.message || "Erreur lors de la mise à jour du statut.");
      }
    } catch (err) {
      console.error('Erreur:', err);
      let errorMsg = "Erreur lors de la mise à jour du statut.";

      if (err.response) {
        switch (err.response.status) {
          case 400:
            errorMsg = err.response.data?.message || "Données invalides pour la résolution.";
            break;
          case 401:
            errorMsg = "Session expirée. Veuillez vous reconnecter.";
            localStorage.removeItem("access_token");
            navigate("/login");
            break;
          case 403:
            errorMsg = "Vous n'avez pas la permission de résoudre cet incident.";
            break;
          case 404:
            errorMsg = "Incident non trouvé.";
            break;
          case 500:
            errorMsg = "Erreur serveur. Veuillez contacter l'administrateur.";
            break;
          default:
            errorMsg = `Erreur ${err.response.status}: ${err.response.data?.message || 'Erreur serveur'}`;
        }
      } else if (err.request) {
        errorMsg = "Serveur inaccessible. Vérifiez votre connexion.";
      }

      setResolveFieldError(errorMsg);
    } finally {
      setResolving(false);
    }
  };

  const exportPDF = async () => {
    if (!incident) return;
    const doc = new jsPDF('p', 'mm', 'a4');

    // En-tête identique aux autres exports PDF de l'app (logo + société + titre + séparateur).
    const logoSize = 24;
    const logoTop = 8;
    let logoBottom = logoTop;
    if (societeLogo) {
      const format = societeLogo.startsWith('data:image/png') ? 'PNG'
        : societeLogo.startsWith('data:image/webp') ? 'WEBP'
        : 'JPEG';
      try {
        doc.addImage(societeLogo, format, 14, logoTop, logoSize, logoSize);
        logoBottom = logoTop + logoSize;
      } catch (e) {
        // Logo illisible : on continue sans bloquer l'export.
      }
    }

    const infoX = 14 + logoSize + 6;
    let infoY = logoTop + 6;
    if (nomSociete) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(nomSociete, infoX, infoY);
      infoY += 5;
    }
    if (societeInfoLigne) {
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100);
      doc.text(societeInfoLigne, infoX, infoY);
      doc.setTextColor(0);
      infoY += 5;
    }

    let y = Math.max(logoBottom, infoY) + 6;

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`Fiche Incident — Ticket #${incident.numero_ticket_incident || incident.id_incident}`, 105, y, { align: 'center' });
    y += 5;

    const sepY = y + 3;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(14, sepY, 196, sepY);
    y = sepY + 8;

    const pageHeight = doc.internal.pageSize.getHeight();
    const marginLeft = 14;
    const marginRight = 196;
    const lineHeight = 6;

    const ensureSpace = (needed) => {
      if (y + needed > pageHeight - 15) {
        doc.addPage();
        y = 15;
      }
    };

    const sectionTitle = (titre) => {
      ensureSpace(10);
      doc.setFillColor(245, 247, 250);
      doc.rect(marginLeft, y - 5, marginRight - marginLeft, 8, 'F');
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(11, 60, 93);
      doc.text(titre, marginLeft + 2, y);
      doc.setTextColor(0);
      y += 8;
    };

    const field = (label, value) => {
      ensureSpace(lineHeight + 2);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      const labelText = `${label} : `;
      // Largeur mesurée AVANT de repasser en police normale : mesurer après
      // (comme avant ce correctif) sous-estime la largeur réelle du label
      // (le gras est plus large que le normal), ce qui faisait chevaucher le
      // texte de la valeur sur la fin du label.
      const labelWidth = doc.getTextWidth(labelText);
      doc.text(labelText, marginLeft, y);
      doc.setFont("helvetica", "normal");
      const valueLines = doc.splitTextToSize(String(value || '-'), marginRight - marginLeft - labelWidth - 2);
      doc.text(valueLines, marginLeft + labelWidth + 2, y);
      y += lineHeight * Math.max(valueLines.length, 1);
    };

    const dateResolus = incident.date_resolus_incident;
    const estResolu = dateResolus && dateResolus !== "0000-00-00 00:00:00";

    sectionTitle("Informations générales");
    field("N° Ticket", incident.numero_ticket_incident);
    field("Date de création", formatDate(incident.date_creer_incident));
    field("Statut", incidentStatutLabel(incident.statut, dateResolus).text);
    field("Niveau", niveauLabel(incident.niveau).text);
    field("Type d'incident", getTypeIncident());
    if (estResolu) {
      const resolveurLabel = incident.resolveur ? `${incident.resolveur.prenom_user || ''} ${incident.resolveur.nom_user || ''}`.trim() : '';
      field("Date de résolution", resolveurLabel ? `${formatDate(dateResolus)} — par ${resolveurLabel}` : formatDate(dateResolus));
    }
    if (incident.date_prise_en_charge) field("Pris en charge le", formatDate(incident.date_prise_en_charge));
    y += 2;

    sectionTitle("Description et action");
    field("Description", incident.description_incident);
    field("Action affectée", incident.action_affecter_incident);
    if (estResolu) {
      field("Actions de résolution", incident.action_resolution_incident || "Aucune action de résolution enregistrée");
    }
    y += 2;

    sectionTitle("Agent concerné");
    field("Nom", getAgentName());
    field("Matricule", incident.agent?.matricule_agent);
    if (incident.agent?.telephone) field("Téléphone", incident.agent.telephone);
    y += 2;

    sectionTitle("Contrôleur");
    field("Nom", getControleurName());
    if (incident.controleur?.email) field("Email", incident.controleur.email);
    if (incident.controleur?.telephone) field("Téléphone", incident.controleur.telephone);
    y += 2;

    sectionTitle("Site");
    field("Nom du site", getSiteName());
    field("Adresse", getSiteAdresse());
    if (incident.latitude && incident.longitude) {
      field("Coordonnées GPS", `${parseFloat(incident.latitude).toFixed(5)}, ${parseFloat(incident.longitude).toFixed(5)}`);
    }

    if (incident.photo_path) {
      const base64 = await imageUrlToBase64(`${API_BASE_URL}/${incident.photo_path}`);
      if (base64) {
        y += 2;
        ensureSpace(70);
        sectionTitle("Photo de l'incident");
        try {
          doc.addImage(base64, 'JPEG', marginLeft, y, 90, 60);
          y += 65;
        } catch (e) {
          // Format non supporté par jsPDF : on continue sans bloquer l'export.
        }
      }
    }

    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Généré le ${new Date().toLocaleString('fr-FR')}`, marginLeft, pageHeight - 10);
    doc.setTextColor(0);

    doc.save(`Fiche_Incident_${incident.numero_ticket_incident || incident.id_incident}.pdf`);
  };

  useEffect(() => {
    fetchIncidentDetail();
  }, [fetchIncidentDetail]);

  // Fonctions d'extraction des noms
  const getAgentName = () => {
    if (!incident?.agent) return 'N/A';
    const { agent } = incident;
    
    if (agent.nom_agent && agent.prenom_agent) {
      return `${agent.prenom_agent} ${agent.nom_agent}`;
    }
    if (agent.nom_agent) return agent.nom_agent;
    return 'Agent';
  };

  const getSiteName = () => {
    if (!incident?.site) return 'N/A';
    return incident.site.nom || incident.site.nom_site || 'Site';
  };

  const getSiteAdresse = () => {
    if (!incident?.site) return '';
    return incident.site.adresse || incident.site.addresse_site || '';
  };

  const getControleurName = () => {
    if (!incident?.controleur) return 'N/A';
    const { controleur } = incident;
    
    if (controleur.prenom_user && controleur.nom_user) {
      return `${controleur.prenom_user} ${controleur.nom_user}`;
    }
    if (controleur.nom_user) return controleur.nom_user;
    return 'N/A';
  };

  const getTypeIncident = () => {
    if (!incident?.type_incident) return 'N/A';
    return incident.type_incident.type || incident.type_incident.libelle || 'N/A';
  };

  const isIncidentResolved = incident?.date_resolus_incident !== null && 
                              incident?.date_resolus_incident !== undefined && 
                              incident?.date_resolus_incident !== "0000-00-00 00:00:00";

  const incidentStatut = incidentStatutLabel(incident?.statut, incident?.date_resolus_incident);
  const incidentNiveau = niveauLabel(incident?.niveau);
  const showResolveButton = !isIncidentResolved;

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails de l'incident</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/incidents" style={{ color: COLORS.primary }}>Incidents</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Chargement...</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="row">
            <div className="col-lg-12">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body text-center py-5">
                  <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                  <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des détails...</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (error || !incident) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails de l'incident</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/incidents" style={{ color: COLORS.primary }}>Incidents</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.danger }}>Erreur</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="row">
            <div className="col-lg-12">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body">
                  <div className="alert alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: COLORS.danger,
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.danger}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">
                        <h6 className="mb-1" style={{ color: '#c62828' }}>Erreur</h6>
                        <p className="mb-0">{error || "Incident non trouvé"}</p>
                      </div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setError("")}
                      ></button>
                    </div>
                  </div>
                  <div className="text-center">
                    <button 
                      className="btn fw-semibold"
                      onClick={() => navigate("/incidents")}
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 24px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.primary;
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.primary;
                      }}
                    >
                      <i className="bi bi-arrow-left me-2"></i> Retour aux incidents
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails de l'incident</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/incidents" style={{ color: COLORS.primary }}>Incidents</Link>
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="card-title mb-1" style={{ 
                      color: COLORS.primary,
                      borderBottom: `2px solid ${COLORS.lightBg}`,
                      paddingBottom: '16px',
                      width: '100%'
                    }}>
                      <div className="d-flex align-items-center">
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                          width: '36px',
                          height: '36px',
                          backgroundColor: '#ffebee',
                          color: COLORS.danger
                        }}>
                          <i className="bi bi-exclamation-triangle"></i>
                        </div>
                        Détails de l'incident
                      </div>
                    </h5>
                  </div>
                  
                  <div className="d-flex gap-2">
                    <button
                      className="btn fw-semibold"
                      onClick={() => navigate("/incidents")}
                      title="Retour à la liste des incidents"
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 24px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#E3F2FD';
                        e.target.style.borderColor = COLORS.secondary;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.borderColor = COLORS.primary;
                      }}
                    >
                      <i className="bi bi-arrow-left me-2"></i> Retour
                    </button>

                    <button
                      className="btn fw-semibold"
                      onClick={exportPDF}
                      title="Télécharger la fiche PDF de l'incident"
                      style={{
                        color: 'white',
                        backgroundColor: COLORS.secondary,
                        borderColor: COLORS.secondary,
                        borderRadius: '8px',
                        padding: '10px 24px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.primary;
                        e.target.style.borderColor = COLORS.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = COLORS.secondary;
                        e.target.style.borderColor = COLORS.secondary;
                      }}
                    >
                      <i className="bi bi-file-earmark-pdf me-2"></i> Fiche PDF
                    </button>

                    {showResolveButton && (
                      <button
                        className="btn fw-semibold"
                        onClick={handleMarkAsResolved}
                        disabled={resolving}
                        title="Marquer cet incident comme résolu"
                        style={{
                          color: 'white',
                          backgroundColor: resolving ? '#B0BEC5' : COLORS.success,
                          borderColor: resolving ? '#B0BEC5' : COLORS.success,
                          borderRadius: '8px',
                          padding: '10px 24px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!resolving) {
                            e.target.style.backgroundColor = '#43a047';
                            e.target.style.borderColor = '#43a047';
                            e.target.style.transform = 'translateY(-2px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!resolving) {
                            e.target.style.backgroundColor = COLORS.success;
                            e.target.style.borderColor = COLORS.success;
                            e.target.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        {resolving ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                            Traitement...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle me-1"></i>
                            Marquer résolu
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {successMessage && (
                  <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#e8f5e9',
                    borderColor: COLORS.success,
                    color: '#2e7d32',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.success}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-check-circle me-2"></i>
                      <div className="flex-grow-1">{successMessage}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setSuccessMessage("")}
                      ></button>
                    </div>
                  </div>
                )}

                {apiError && (
                  <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: COLORS.danger,
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.danger}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">{apiError}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setApiError("")}
                      ></button>
                    </div>
                  </div>
                )}

                <div className="row">
                  {/* Informations générales */}
                  <div className="col-lg-6">
                    <GeneralInfoCard 
                      incident={incident}
                      incidentStatut={incidentStatut}
                      incidentNiveau={incidentNiveau}
                    />

                    {/* Description de l'incident */}
                    <DescriptionCard incident={incident} />

                    {/* Photo et localisation */}
                    <PhotoLocalisationCard incident={incident} />
                  </div>

                  {/* Colonne droite */}
                  <div className="col-lg-6">
                    {/* Agent */}
                    <AgentCard incident={incident} getAgentName={getAgentName} />

                    {/* Contrôleur */}
                    <ControleurCard incident={incident} getControleurName={getControleurName} />

                    {/* Site */}
                    <SiteCard incident={incident} getSiteName={getSiteName} getSiteAdresse={getSiteAdresse} />

                    {/* Informations supplémentaires */}
                    <AdditionalInfoCard incident={incident} getTypeIncident={getTypeIncident} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal de résolution : la description des actions effectuées est
          obligatoire, on ne peut donc pas résoudre directement depuis le bouton. */}
      {showResolveModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show d-block" tabIndex="-1" style={{ display: 'block' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ borderRadius: '12px' }}>
                <div className="modal-header" style={{ backgroundColor: COLORS.lightBg, borderRadius: '12px 12px 0 0' }}>
                  <h5 className="modal-title mb-0" style={{ color: COLORS.primary, fontWeight: 600 }}>
                    <i className="bi bi-check-circle me-2"></i>Résoudre l'incident
                  </h5>
                  <button type="button" className="btn-close"
                    onClick={() => !resolving && setShowResolveModal(false)}></button>
                </div>
                <div className="modal-body">
                  <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                    Actions effectuées pour résoudre l'incident <span style={{ color: COLORS.danger }}>*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={actionResolutionText}
                    onChange={(e) => setActionResolutionText(e.target.value)}
                    placeholder="Décrivez ce qui a été fait pour résoudre ce problème (agent remplacé, intervention technique, patrouille envoyée, etc.)"
                    autoFocus
                  />
                  {resolveFieldError && <div className="alert alert-danger py-2 mt-2 mb-0 small">{resolveFieldError}</div>}
                </div>
                <div className="modal-footer" style={{ borderTop: `1px solid ${COLORS.lightBg}` }}>
                  <button className="btn" style={{ color: COLORS.darkText, borderColor: '#e0e0e0' }}
                    disabled={resolving}
                    onClick={() => setShowResolveModal(false)}>
                    Annuler
                  </button>
                  <button className="btn fw-semibold" style={{ backgroundColor: COLORS.success, color: 'white' }}
                    disabled={resolving}
                    onClick={confirmMarkAsResolved}>
                    {resolving ? 'Résolution...' : 'Confirmer la résolution'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .table,
        small,
        .alert {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus,
        .form-control:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn,
        .badge {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .badge {
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
        }
        
        /* Animation pour les badges */
        .badge:hover {
          transform: translateY(-1px);
        }
        
        /* Style pour les cartes */
        .card {
          transition: transform 0.2s ease;
        }
        
        .card:hover {
          transform: translateY(-2px);
        }
      `}</style>
    </Layout>
  );
};

// Composant pour les informations générales
const GeneralInfoCard = ({ incident, incidentStatut, incidentNiveau }) => {
  return (
    <div className="card mb-4" style={{ 
      borderRadius: '8px',
      border: `1px solid ${COLORS.lightBg}`,
      boxShadow: 'none'
    }}>
      <div className="card-header" style={{ 
        backgroundColor: COLORS.lightBg,
        borderBottom: `1px solid ${COLORS.lightBg}`,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        padding: '16px'
      }}>
        <h6 className="card-title mb-0" style={{ 
          color: COLORS.primary,
          fontWeight: '600'
        }}>
          <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
            width: '30px',
            height: '30px',
            backgroundColor: '#E3F2FD',
            color: COLORS.primary
          }}>
            <i className="bi bi-info-circle"></i>
          </div>
          Informations générales
        </h6>
      </div>
      <div className="card-body">
        <div className="row mb-3">
          <div className="col-md-6">
            <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
              <i className="bi bi-calendar-plus me-1" style={{ color: COLORS.secondary }}></i>
              Date de création
            </label>
            <p style={{ color: COLORS.darkText }}>
              {formatDate(incident.date_creer_incident)}
            </p>
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
              <i className="bi bi-funnel me-1" style={{ color: COLORS.secondary }}></i>
              Statut
            </label>
            <p>
              <span className="badge fw-semibold" style={{ 
                ...incidentStatut.style,
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '0.85rem'
              }}>
                {incidentStatut.text}
              </span>
            </p>
          </div>
        </div>
        
        <div className="row mb-4">
          <div className="col-md-6">
            <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
              <i className="bi bi-bar-chart me-1" style={{ color: COLORS.secondary }}></i>
              Niveau
            </label>
            <p>
              <span className="badge fw-semibold" style={{ 
                ...incidentNiveau.style,
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '0.85rem'
              }}>
                {incidentNiveau.text}
              </span>
            </p>
          </div>
          <div className="col-md-6">
            <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
              <i className="bi bi-ticket-detailed me-1" style={{ color: COLORS.secondary }}></i>
              N° Ticket
            </label>
            <p>
              {incident.numero_ticket_incident ? (
                <span className="badge fw-semibold" style={{ 
                  backgroundColor: '#E3F2FD',
                  color: COLORS.primary,
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  fontFamily: 'monospace'
                }}>
                  {incident.numero_ticket_incident}
                </span>
              ) : (
                <span className="text-muted">-</span>
              )}
            </p>
          </div>
        </div>

        {incident.date_resolus_incident && incident.date_resolus_incident !== "0000-00-00 00:00:00" && (
          <div className="row mb-3">
            <div className="col-12">
              <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                <i className="bi bi-calendar-check me-1" style={{ color: COLORS.secondary }}></i>
                Date de résolution
              </label>
              <p style={{ color: COLORS.darkText }}>
                {formatDate(incident.date_resolus_incident)}
                {incident.resolveur && (
                  <span className="text-muted"> — par {incident.resolveur.prenom_user} {incident.resolveur.nom_user}</span>
                )}
              </p>
            </div>
          </div>
        )}

        <div className="mt-4 pt-3 border-top" style={{ borderColor: COLORS.lightBg }}>
          <label className="form-label fw-semibold mb-3" style={{ color: COLORS.darkText }}>
            <i className="bi bi-gear me-1" style={{ color: COLORS.secondary }}></i>
            Action affectée
          </label>
          <div className="border rounded p-3" style={{
            borderColor: '#e0e0e0',
            backgroundColor: '#F8F9FA',
            color: COLORS.darkText
          }}>
            {incident.action_affecter_incident || 'Aucune action affectée'}
          </div>
        </div>

        {incident.date_resolus_incident && incident.date_resolus_incident !== "0000-00-00 00:00:00" && (
          <div className="mt-3">
            <label className="form-label fw-semibold mb-3" style={{ color: COLORS.darkText }}>
              <i className="bi bi-check-circle me-1" style={{ color: COLORS.success }}></i>
              Actions de résolution
            </label>
            <div className="border rounded p-3" style={{
              borderColor: '#e0e0e0',
              backgroundColor: '#e8f5e9',
              color: COLORS.darkText
            }}>
              {incident.action_resolution_incident || 'Aucune action de résolution enregistrée'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Composant pour la photo et la localisation de l'incident
const PhotoLocalisationCard = ({ incident }) => {
  const [showMap, setShowMap] = useState(false);

  if (!incident.photo_path && !(incident.latitude && incident.longitude)) {
    return null;
  }

  return (
    <div className="card mb-4" style={{
      borderRadius: '8px',
      border: `1px solid ${COLORS.lightBg}`,
      boxShadow: 'none'
    }}>
      <div className="card-header" style={{
        backgroundColor: COLORS.lightBg,
        borderBottom: `1px solid ${COLORS.lightBg}`,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        padding: '16px'
      }}>
        <h6 className="card-title mb-0" style={{ color: COLORS.primary, fontWeight: '600' }}>
          <i className="bi bi-camera me-2"></i>
          Photo et localisation
        </h6>
      </div>
      <div className="card-body">
        {incident.photo_path && (
          <div className="mb-3">
            <img
              src={`${API_BASE_URL}/${incident.photo_path}`}
              alt="Photo de l'incident"
              className="img-fluid rounded"
              style={{ maxHeight: '320px', border: `1px solid ${COLORS.lightBg}` }}
            />
          </div>
        )}
        {incident.latitude && incident.longitude && (
          <>
            <button
              type="button"
              className="btn btn-sm"
              style={{ color: COLORS.primary, borderColor: COLORS.primary, backgroundColor: 'transparent' }}
              onClick={() => setShowMap(true)}
            >
              <i className="bi bi-geo-alt me-2"></i>
              Voir sur la carte ({parseFloat(incident.latitude).toFixed(5)}, {parseFloat(incident.longitude).toFixed(5)})
            </button>
            <MapViewer
              show={showMap}
              onHide={() => setShowMap(false)}
              latitude={incident.latitude}
              longitude={incident.longitude}
              siteName="Lieu de l'incident"
            />
          </>
        )}
      </div>
    </div>
  );
};

// Composant pour la description
const DescriptionCard = ({ incident }) => {
  return (
    <div className="card mb-4" style={{ 
      borderRadius: '8px',
      border: `1px solid ${COLORS.lightBg}`,
      boxShadow: 'none'
    }}>
      <div className="card-header" style={{ 
        backgroundColor: COLORS.lightBg,
        borderBottom: `1px solid ${COLORS.lightBg}`,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        padding: '16px'
      }}>
        <h6 className="card-title mb-0" style={{ 
          color: COLORS.primary,
          fontWeight: '600'
        }}>
          <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
            width: '30px',
            height: '30px',
            backgroundColor: '#E3F2FD',
            color: COLORS.primary
          }}>
            <i className="bi bi-card-text"></i>
          </div>
          Description
        </h6>
      </div>
      <div className="card-body">
        <div className="border rounded p-3" style={{ 
          borderColor: '#e0e0e0',
          backgroundColor: '#F8F9FA',
          color: COLORS.darkText,
          minHeight: '120px'
        }}>
          {incident.description_incident || 'Aucune description disponible'}
        </div>
      </div>
    </div>
  );
};

// Composant pour l'agent
const AgentCard = ({ incident, getAgentName }) => {
  return (
    <div className="card mb-4" style={{ 
      borderRadius: '8px',
      border: `1px solid ${COLORS.lightBg}`,
      boxShadow: 'none'
    }}>
      <div className="card-header" style={{ 
        backgroundColor: COLORS.lightBg,
        borderBottom: `1px solid ${COLORS.lightBg}`,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        padding: '16px'
      }}>
        <h6 className="card-title mb-0" style={{ 
          color: COLORS.primary,
          fontWeight: '600'
        }}>
          <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
            width: '30px',
            height: '30px',
            backgroundColor: '#E3F2FD',
            color: COLORS.primary
          }}>
            <i className="bi bi-person-badge"></i>
          </div>
          Agent
        </h6>
      </div>
      <div className="card-body">
        <div className="d-flex align-items-center">
          <div className="me-3">
            <div className="rounded-circle d-flex align-items-center justify-content-center fw-semibold"
              style={{ 
                width: '60px',
                height: '60px',
                fontSize: '20px',
                backgroundColor: COLORS.primary,
                color: 'white',
                boxShadow: '0 2px 4px rgba(11, 60, 93, 0.1)'
              }}>
              {getAgentName().charAt(0).toUpperCase() || 'A'}
            </div>
          </div>
          <div>
            <h5 className="mb-1" style={{ color: COLORS.darkText }}>
              {getAgentName()}
            </h5>
            <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
              <i className="bi bi-person-badge me-1"></i>
              Agent de sécurité
            </p>
            <p className="mb-2" style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
              <i className="bi bi-card-text me-1" style={{ color: '#6c757d' }}></i>
              Matricule: <span className="fw-semibold">{incident.agent?.matricule_agent || '-'}</span>
            </p>
            {incident.agent?.telephone && (
              <p className="mb-0" style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                <i className="bi bi-telephone me-1" style={{ color: '#6c757d' }}></i>
                {incident.agent.telephone}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant pour le contrôleur
const ControleurCard = ({ incident, getControleurName }) => {
  return (
    <div className="card mb-4" style={{ 
      borderRadius: '8px',
      border: `1px solid ${COLORS.lightBg}`,
      boxShadow: 'none'
    }}>
      <div className="card-header" style={{ 
        backgroundColor: COLORS.lightBg,
        borderBottom: `1px solid ${COLORS.lightBg}`,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        padding: '16px'
      }}>
        <h6 className="card-title mb-0" style={{ 
          color: COLORS.primary,
          fontWeight: '600'
        }}>
          <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
            width: '30px',
            height: '30px',
            backgroundColor: '#fff3e0',
            color: '#f57c00'
          }}>
            <i className="bi bi-shield-check"></i>
          </div>
          Contrôleur
        </h6>
      </div>
      <div className="card-body">
        {getControleurName() !== 'N/A' ? (
          <div className="d-flex align-items-center">
            <div className="me-3">
              <div className="rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                style={{ 
                  width: '60px',
                  height: '60px',
                  fontSize: '20px',
                  backgroundColor: '#fff3e0',
                  color: '#f57c00',
                  boxShadow: '0 2px 4px rgba(245, 124, 0, 0.1)'
                }}>
                {getControleurName().charAt(0).toUpperCase() || 'C'}
              </div>
            </div>
            <div>
              <h5 className="mb-1" style={{ color: COLORS.darkText }}>
                {getControleurName()}
              </h5>
              <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                <i className="bi bi-shield-check me-1"></i>
                Contrôleur
              </p>
              {incident.controleur?.email && (
                <p className="mb-2" style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                  <i className="bi bi-envelope me-1" style={{ color: '#6c757d' }}></i>
                  {incident.controleur.email}
                </p>
              )}
              {incident.controleur?.telephone && (
                <p className="mb-0" style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                  <i className="bi bi-telephone me-1" style={{ color: '#6c757d' }}></i>
                  {incident.controleur.telephone}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-3">
            <div className="rounded-circle d-inline-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
              width: '60px',
              height: '60px',
              backgroundColor: '#F5F7FA',
              color: '#B0BEC5'
            }}>
              <i className="bi bi-person-x fs-1"></i>
            </div>
            <p className="text-muted mb-0">Aucun contrôleur assigné</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Composant pour le site
const SiteCard = ({ incident, getSiteName, getSiteAdresse }) => {
  return (
    <div className="card mb-4" style={{ 
      borderRadius: '8px',
      border: `1px solid ${COLORS.lightBg}`,
      boxShadow: 'none'
    }}>
      <div className="card-header" style={{ 
        backgroundColor: COLORS.lightBg,
        borderBottom: `1px solid ${COLORS.lightBg}`,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        padding: '16px'
      }}>
        <h6 className="card-title mb-0" style={{ 
          color: COLORS.primary,
          fontWeight: '600'
        }}>
          <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
            width: '30px',
            height: '30px',
            backgroundColor: '#E3F2FD',
            color: COLORS.primary
          }}>
            <i className="bi bi-building"></i>
          </div>
          Site
        </h6>
      </div>
      <div className="card-body">
        <h5 className="mb-3" style={{ color: COLORS.darkText }}>
          {getSiteName()}
        </h5>
        <div className="mb-3">
          <label className="form-label fw-semibold mb-2" style={{ color: COLORS.darkText }}>
            <i className="bi bi-geo-alt me-1" style={{ color: COLORS.secondary }}></i>
            Adresse
          </label>
          <p style={{ color: COLORS.darkText, lineHeight: '1.5' }}>
            {getSiteAdresse() || 'Adresse non disponible'}
          </p>
        </div>
      </div>
    </div>
  );
};

// Composant pour les informations supplémentaires
const AdditionalInfoCard = ({ incident, getTypeIncident }) => {
  return (
    <div className="card mb-4" style={{ 
      borderRadius: '8px',
      border: `1px solid ${COLORS.lightBg}`,
      boxShadow: 'none'
    }}>
      <div className="card-header" style={{ 
        backgroundColor: COLORS.lightBg,
        borderBottom: `1px solid ${COLORS.lightBg}`,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        padding: '16px'
      }}>
        <h6 className="card-title mb-0" style={{ 
          color: COLORS.primary,
          fontWeight: '600'
        }}>
          <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
            width: '30px',
            height: '30px',
            backgroundColor: '#E3F2FD',
            color: COLORS.primary
          }}>
            <i className="bi bi-clock-history"></i>
          </div>
          Informations supplémentaires
        </h6>
      </div>
      <div className="card-body">
        <div className="mb-3">
          <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
            Type d'incident
          </label>
          <p>
            {incident.type_incident ? (
              <span className="badge fw-semibold" style={{ 
                backgroundColor: '#E3F2FD',
                color: COLORS.primary,
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '0.85rem'
              }}>
                {getTypeIncident()}
              </span>
            ) : (
              <span className="text-muted">-</span>
            )}
          </p>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
              <i className="bi bi-calendar-plus me-1" style={{ color: COLORS.secondary }}></i>
              Créé le
            </label>
            <p style={{ color: COLORS.darkText }}>
              {formatDate(incident.created_at)}
            </p>
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
              <i className="bi bi-calendar-check me-1" style={{ color: COLORS.secondary }}></i>
              Mis à jour le
            </label>
            <p style={{ color: COLORS.darkText }}>
              {formatDate(incident.updated_at)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentDetail;